import { useEffect } from 'react';
import { Link } from 'react-router';
import { LandingPagesNavbar, LandingPagesFooter } from '@podus/ui';

/**
 * 404 page.
 *
 * A static SPA can't return a real HTTP 404 — the host rewrites every unknown path to index.html
 * with a 200. Without this route those URLs rendered a blank page that Google would index as a
 * soft 404 carrying the homepage's title. Rendering real "not found" content and injecting
 * `noindex` is the documented way to signal it from the client.
 */
export default function NotFound() {
    useEffect(() => {
        // Override the existing robots tags rather than appending a second one — conflicting tags
        // resolve to the most restrictive, but a single unambiguous value is easier to reason about.
        const tags = [...document.querySelectorAll<HTMLMetaElement>('meta[name="robots"], meta[name="googlebot"]')];
        const previous = tags.map((tag) => ({ tag, content: tag.content }));
        tags.forEach((tag) => {
            tag.content = 'noindex, follow';
        });

        const previousTitle = document.title;
        document.title = 'Page not found — Podus AI';

        return () => {
            previous.forEach(({ tag, content }) => {
                tag.content = content;
            });
            document.title = previousTitle;
        };
    }, []);

    return (
        <div className="page-scroll-root bg-white">
            <LandingPagesNavbar product="podus-ai" />

            <main className="mx-auto flex min-h-[70dvh] max-w-[1920px] items-center px-4 py-20 lg:px-16 xl:px-20">
                <div className="mx-auto max-w-2xl text-center">
                    <p className="text-[clamp(4rem,12vw,8rem)] font-bold leading-none text-[#AEAEAE]">404</p>

                    <h1 className="mt-2 text-[clamp(1.5rem,4vw,2.5rem)] font-normal italic leading-tight text-secondary">
                        Page not found
                    </h1>

                    <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-[#747474]">
                        The page you are looking for does not exist or may have moved. Let&apos;s get you back to
                        something useful.
                    </p>

                    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 rounded-full bg-secondary px-8 py-3.5 text-[14px] font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <span aria-hidden="true">&larr;</span>
                            Back to Podus AI
                        </Link>
                    </div>
                </div>
            </main>

            <LandingPagesFooter />
        </div>
    );
}
